import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEntityScope } from '../../hooks/useEntityScope.js';
import EntityBadge from '../EntityBadge.jsx';
import { formatCurrency } from '../../lib/format.js';

/**
 * Panneau de configuration de la refacturation intercompany.
 *
 * Composant contrôlé : le parent gère le state via `value` / `onChange`.
 *
 * Props :
 *   value          — config actuelle { enabled, rule, vat_mode } ou null
 *   onChange       — (newConfig: object | null) => void
 *   fromEntityId   — UUID de l'entité émettrice (filtrée de la liste des débitrices)
 *   amountCents    — montant TTC en centimes pour l'aperçu live (optionnel)
 *   sourceVatRate  — taux TVA de la dépense source en % (0/5.5/10/20/...) — sert
 *                    à extraire le HT du TTC pour l'aperçu, pour caler la
 *                    refac × pct sur le HT et pas sur le TTC.
 */
export default function IntercompanyConfigPanel({ value, onChange, fromEntityId, amountCents, sourceVatRate }) {
  const { memberships } = useEntityScope();

  // Entités du groupe autres que l'émettrice
  const otherEntities = memberships.filter((m) => m.entity_id !== fromEntityId);

  // State local pour les inputs % en cours de frappe (valeurs intermédiaires)
  const [localPcts, setLocalPcts] = useState({});

  const enabled = value?.enabled === true;
  const ruleType = value?.rule?.type ?? 'ca_weighted';
  const shares = value?.rule?.shares ?? {};
  const vatMode = value?.vat_mode ?? 'exempt';

  function toggleEnabled() {
    if (enabled) {
      // Désactivation → null au parent
      setLocalPcts({});
      onChange(null);
    } else {
      // Activation avec defaults
      onChange({
        enabled: true,
        rule: { type: 'ca_weighted', shares: {} },
        vat_mode: 'exempt',
      });
    }
  }

  function setRuleType(type) {
    onChange({
      ...value,
      rule: {
        type,
        shares: type === 'ca_weighted' ? {} : shares,
      },
    });
  }

  function setVatMode(mode) {
    onChange({ ...value, vat_mode: mode });
  }

  function handlePctInput(entityId, raw) {
    // Stocke la valeur locale brute pour les états intermédiaires (ex : "1." ou vide)
    setLocalPcts((prev) => ({ ...prev, [entityId]: raw }));

    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      const newShares = { ...shares, [entityId]: parsed };
      onChange({
        ...value,
        rule: { ...value.rule, shares: newShares },
      });
    }
  }

  function pctDisplayValue(entityId) {
    if (entityId in localPcts) return localPcts[entityId];
    const s = shares[entityId];
    return s != null ? String(s) : '';
  }

  // Somme des % alloués aux débitrices
  const allocatedPct = otherEntities.reduce((acc, m) => {
    const v = shares[m.entity_id];
    return acc + (v != null ? v : 0);
  }, 0);
  const residualPct = Math.max(0, 100 - allocatedPct);

  // Aperçu live si amountCents fourni et activé
  const showPreview = enabled && amountCents != null && amountCents > 0;

  // Extraction du HT source à partir du TTC saisi + taux TVA source.
  // amountCents porté par le form est le TTC ; on retire la TVA source pour
  // que la part débitrice (× pct) soit calée sur le HT, pas sur le TTC.
  // Si sourceVatRate non fourni ou = 0 (dépense non assujettie) → HT = TTC.
  const sourceVatNum = Number(sourceVatRate ?? 0);
  const htSourceCents = sourceVatNum > 0 && amountCents != null
    ? Math.round(amountCents / (1 + sourceVatNum / 100))
    : (amountCents ?? 0);

  function computePreviewLines() {
    if (!showPreview) return [];
    const lines = [];
    const refacVatMultiplier = vatMode === 'standard' ? 1.2 : 1;

    if (ruleType === 'fixed_pct') {
      otherEntities.forEach((m) => {
        const pct = shares[m.entity_id] ?? 0;
        if (pct <= 0) return;
        const htCents = Math.round(htSourceCents * pct / 100);
        const ttcCents = Math.round(htCents * refacVatMultiplier);
        lines.push({ name: m.name, entityId: m.entity_id, pct, htCents, ttcCents });
      });
    } else {
      // ca_weighted : on indique juste que la répartition sera automatique
      otherEntities.forEach((m) => {
        lines.push({ name: m.name, entityId: m.entity_id, pct: null, htCents: null, ttcCents: null });
      });
    }

    return lines;
  }

  const previewLines = computePreviewLines();

  const panelId = 'intercompany-config-panel';

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
      {/* Header toggle */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Refacturer aux autres entités du groupe"
            onClick={toggleEnabled}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ${
              enabled ? 'bg-slate-800' : 'bg-slate-300'
            }`}
            aria-controls={panelId}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                enabled ? 'translate-x-4' : 'translate-x-0'
              }`}
              aria-hidden
            />
          </button>
          <span className="text-sm font-medium text-slate-700">
            Refacturer aux autres entités du groupe
          </span>
        </div>
        {enabled && (
          <span className="text-slate-400" aria-hidden>
            <ChevronUp size={16} />
          </span>
        )}
        {!enabled && otherEntities.length > 0 && (
          <span className="text-slate-400" aria-hidden>
            <ChevronDown size={16} />
          </span>
        )}
      </div>

      {/* Panneau déroulé */}
      {enabled && (
        <div id={panelId} className="border-t border-slate-200 px-4 py-4 space-y-5 bg-white">
          {otherEntities.length === 0 ? (
            <p className="text-sm text-slate-500">
              Aucune autre entité dans le groupe — la refacturation n'est pas disponible.
            </p>
          ) : (
            <>
              {/* Mode de clé de répartition */}
              <fieldset className="space-y-2">
                <legend className="text-xs font-medium text-slate-700">
                  Mode de répartition
                </legend>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="interco-rule-type"
                      value="ca_weighted"
                      checked={ruleType === 'ca_weighted'}
                      onChange={() => setRuleType('ca_weighted')}
                      className="mt-0.5 accent-slate-800 focus-visible:ring-2 focus-visible:ring-slate-500"
                    />
                    <div>
                      <span className="text-sm text-slate-800 font-medium">
                        CA pondéré (auto)
                      </span>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Répartition calculée sur les 3 derniers mois de CA de chaque entité
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="interco-rule-type"
                      value="fixed_pct"
                      checked={ruleType === 'fixed_pct'}
                      onChange={() => setRuleType('fixed_pct')}
                      className="mt-0.5 accent-slate-800 focus-visible:ring-2 focus-visible:ring-slate-500"
                    />
                    <div>
                      <span className="text-sm text-slate-800 font-medium">
                        % fixe par entité
                      </span>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Pourcentage saisi manuellement pour chaque entité débitrice
                      </p>
                    </div>
                  </label>
                </div>
              </fieldset>

              {/* Inputs % (mode fixed_pct seulement) */}
              {ruleType === 'fixed_pct' && (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-slate-700">
                    Part de chaque entité débitrice (%)
                  </p>
                  {otherEntities.map((m) => (
                    <div key={m.entity_id} className="flex items-center gap-3">
                      <EntityBadge slug={m.slug} name={m.name} />
                      <span className="flex-1 text-sm text-slate-700 min-w-0 truncate">
                        {m.name}
                      </span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={pctDisplayValue(m.entity_id)}
                          onChange={(e) => handlePctInput(m.entity_id, e.target.value)}
                          className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                          aria-label={`Part de ${m.name} (%)`}
                          placeholder="0"
                        />
                        <span className="text-sm text-slate-500">%</span>
                      </div>
                    </div>
                  ))}

                  {/* Part résiduelle émettrice */}
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 border border-slate-200">
                    <span className="text-xs text-slate-500">
                      Part résiduelle émettrice (100 − Σ)
                    </span>
                    <span
                      className={`text-sm font-semibold tabular-nums ${
                        residualPct < 0
                          ? 'text-red-600'
                          : residualPct === 0
                          ? 'text-slate-500'
                          : 'text-slate-800'
                      }`}
                    >
                      {residualPct.toFixed(1)} %
                    </span>
                  </div>
                </div>
              )}

              {/* Mode TVA */}
              <fieldset className="space-y-2">
                <legend className="text-xs font-medium text-slate-700">
                  Régime TVA sur la refacturation
                </legend>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="interco-vat-mode"
                      value="exempt"
                      checked={vatMode === 'exempt'}
                      onChange={() => setVatMode('exempt')}
                      className="mt-0.5 accent-slate-800 focus-visible:ring-2 focus-visible:ring-slate-500"
                    />
                    <div>
                      <span className="text-sm text-slate-800 font-medium">
                        Exonéré (art. 261 B — partage de frais)
                      </span>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Refacturation à l'exact (pas de marge, pas de TVA)
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="interco-vat-mode"
                      value="standard"
                      checked={vatMode === 'standard'}
                      onChange={() => setVatMode('standard')}
                      className="mt-0.5 accent-slate-800 focus-visible:ring-2 focus-visible:ring-slate-500"
                    />
                    <div>
                      <span className="text-sm text-slate-800 font-medium">
                        Standard 20 % (prestation avec valeur ajoutée)
                      </span>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        TVA collectée sur le montant refacturé
                      </p>
                    </div>
                  </label>
                </div>
              </fieldset>

              {/* Aperçu live */}
              {showPreview && previewLines.length > 0 && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-3 space-y-1.5">
                  <p className="text-xs font-semibold text-blue-800 mb-2">
                    Aperçu sur {formatCurrency(amountCents)}
                  </p>
                  {ruleType === 'ca_weighted' ? (
                    <p className="text-xs text-blue-700">
                      La répartition sera calculée automatiquement en fonction du CA
                      des 3 derniers mois au moment de la génération.
                    </p>
                  ) : (
                    <>
                      {previewLines.map((line) => (
                        <div key={line.entityId} className="flex items-center justify-between text-xs">
                          <span className="text-blue-700">
                            <EntityBadge slug={undefined} name={line.name} className="mr-1.5" />
                            {line.name}
                            {line.pct != null && (
                              <span className="text-blue-500 ml-1">({line.pct} %)</span>
                            )}
                          </span>
                          {line.htCents != null ? (
                            <span className="tabular-nums font-medium text-blue-800">
                              {vatMode === 'standard'
                                ? `${formatCurrency(line.htCents)} HT (${formatCurrency(line.ttcCents)} TTC)`
                                : formatCurrency(line.htCents)}
                            </span>
                          ) : (
                            <span className="text-blue-500 italic">auto</span>
                          )}
                        </div>
                      ))}
                      {/* Part émettrice (HT) */}
                      {residualPct > 0 && (
                        <div className="flex items-center justify-between text-xs border-t border-blue-200 pt-1.5 mt-1.5">
                          <span className="text-blue-700 font-medium">
                            Reste à la charge de l'émettrice ({residualPct.toFixed(1)} %)
                          </span>
                          <span className="tabular-nums font-semibold text-blue-800">
                            {formatCurrency(Math.round(htSourceCents * residualPct / 100))}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
